import Promise from 'bluebird';
import { isFunction, isNotNil } from '../../util/object';
import { cancelTask, deferTask } from '../../util/window';
import CanceledRequestError from '../../errors/CanceledRequestError';

/**
 * Task - a utility class that gives control over when to run some code.
 */
export default class Task {
  _taskCancelled = false;
  _taskError = null;
  _taskHasRun = false;
  _taskMethod = null;
  _taskNumber = null;
  _taskPromise = null;
  _taskReject = null;
  _taskResolve = null;
  _taskValue = null;

  /**
   * @param {Function} taskMethod - a function which, when called, runs the task. It can
   * return a promise or a value. The resulting value (from the promise chain or directly)
   * will be used upon resolution
   */
  constructor(taskMethod) {
    if (!isFunction(taskMethod)) {
      throw new Error('Cannot create a task without a task method');
    }
    this._taskMethod = taskMethod;

    // Set up promise resolution and rejection handlers.
    this._taskPromise = new Promise((resolve, reject) => {
      this._taskResolve = resolve;
      this._taskReject = reject;
    });
  }

  /**
   * run - runs a task, can only be called one time. If called a subsequent time
   * the original promise is returned.
   *
   * @param {*} inputValue - value passed in to the tasks taskMethod
   * @param {number} delayTimeMs - ms to wait before running task
   * @returns {Promise<any>}
   */
  run(inputValue, delayTimeMs = 0) {
    if (!isFunction(this._taskMethod)) {
      throw new Error('Cannot run task without a task method');
    }

    if (this._taskHasRun || this._taskCancelled) return this._taskPromise;

    // Create an inner method that runs the taskMethod
    const innerTask = () => {
      try {
        // Run the method, use its value as resolution to a promise.
        const taskPromise = Promise.resolve(this._taskMethod(inputValue));

        const innerTaskThenHandler = (taskValue) => {
          this._taskValue = taskValue;
          this._taskResolve(this._taskValue);
        };

        const innerTaskCatchHandler = (taskError) => {
          this._taskError = taskError;
          this._taskReject(taskError);
          throw taskError;
        };

        taskPromise.then(innerTaskThenHandler).catch(innerTaskCatchHandler);
        // try/catch does not catch promise's errors
      } catch (taskError) {
        this._taskError = taskError;
        this._taskReject(this._taskError);
      }
    };

    // Schedule running the inner task
    this._taskNumber = deferTask(innerTask, delayTimeMs);

    this._taskHasRun = true;
    return this._taskPromise;
  }

  /**
   * cancel - if a task has been called to run but hasn't yet (a very small window)
   * this will cancel the task
   */
  cancel() {
    if (!this._taskCancelled && isNotNil(this._taskNumber)) {
      this._taskCancelled = true;
      cancelTask(this._taskNumber);
      this._taskNumber = null;
      this._taskReject(new CanceledRequestError());
    }
  }

  /**
   * taskError - if the task was cancelled or had some other error the error value is available here
   * @returns {Error}
   */
  get taskError() {
    return this._taskError;
  }

  /**
   * taskValue - when the task has completed this will store the value returned form the taskMethod
   * @returns {*}
   */
  get taskValue() {
    return this._taskValue;
  }

  /**
   * @param {*} inTask
   * @returns {boolean}
   */
  static isTask(inTask) {
    return inTask instanceof Task;
  }
}
